import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { C } from "../constants/colors";
import { TYPE_STYLE } from "../constants/opportunities";
import { formatDate } from "../lib/utils";
import { css } from "../constants/globalStyles";

const STATUS_COLORS = {
  pending:     { bg: C.amberL, c: C.amber },
  shortlisted: { bg: C.amberL, c: C.amber },
  accepted:    { bg: C.sageL,  c: C.sageD },
  declined:    { bg: C.roseL,  c: C.rose  },
};

const STATUS_MESSAGES = {
  pending: {
    icon: "⏳",
    heading: "Your application is under review",
    body: "The gallery is reviewing submitted applications. We'll let you know as soon as there's an update.",
    bg: C.amberL,
    border: C.amber,
  },
  shortlisted: {
    icon: "⭐",
    heading: "You've been shortlisted",
    body: "Great news — the gallery has shortlisted your application. They're reviewing shortlisted artists and will be in touch soon.",
    bg: C.amberL,
    border: C.amber,
  },
  accepted: {
    icon: "✓",
    heading: "Congratulations — you've been accepted",
    body: "Your application has been accepted. The gallery will be in touch to discuss next steps.",
    bg: C.sageL,
    border: C.sage,
  },
  declined: {
    icon: "◌",
    heading: "Thank you for applying",
    body: "The gallery wasn't able to include your work in this call, but your practice is valued. Keep submitting — every application builds your profile in the industry.",
    bg: C.roseL,
    border: C.rose,
  },
};

const DELIVERY_OPTIONS = [
  { value: "drop_off",       label: "Drop-off" },
  { value: "gallery_pickup", label: "Gallery pickup" },
  { value: "shipping",       label: "Shipping" },
];

export default function ArtistApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [application, setApplication] = useState(null);
  const [opportunity, setOpportunity] = useState(null);

  // Consignment agreement
  const [agreement, setAgreement] = useState(null);
  const [agrSaving, setAgrSaving] = useState(false);
  const [agrError, setAgrError] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [preferredTiming, setPreferredTiming] = useState("");

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError("Please log in to view this application.");
        setLoading(false);
        return;
      }

      const { data, error: err } = await supabase
        .from("applications")
        .select("*, opportunities(*)")
        .eq("id", id)
        .single();

      if (err || !data) {
        setError("Application not found.");
        setLoading(false);
        return;
      }
      if (data.artist_id !== user.id) {
        setError("You don't have permission to view this application.");
        setLoading(false);
        return;
      }

      setApplication(data);
      setOpportunity(data.opportunities);

      // Fetch agreement whenever the opportunity offers consignment
      if (data.opportunities?.consignment_offered) {
        const { data: agrData } = await supabase
          .from("consignment_agreements")
          .select("*")
          .eq("application_id", id)
          .maybeSingle();
        if (agrData) {
          setAgreement(agrData);
          setDeliveryMethod(agrData.delivery_method || "");
          setPreferredTiming(agrData.preferred_timing || "");
        }
      }

      setLoading(false);
    }
    load();
  }, [id]);

  const handleAcknowledge = async () => {
    if (!agreement) return;
    setAgrSaving(true);
    setAgrError(null);
    const { error: err } = await supabase
      .from("consignment_agreements")
      .update({
        artist_confirmed: true,
        artist_confirmed_at: new Date().toISOString(),
        delivery_method: deliveryMethod || null,
        preferred_timing: preferredTiming.trim() || null,
      })
      .eq("id", agreement.id);
    if (err) { setAgrError(err.message); setAgrSaving(false); return; }
    setAgreement(a => ({ ...a, artist_confirmed: true, delivery_method: deliveryMethod, preferred_timing: preferredTiming }));
    setAgrSaving(false);
  };

  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.parch }}>
          <span style={{ fontFamily:"'DM Mono',monospace",fontSize:".7rem",color:C.muted,letterSpacing:"2px",textTransform:"uppercase" }}>Loading…</span>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <style>{css}</style>
        <div style={{ minHeight:"100vh",background:C.parch,display:"flex",alignItems:"center",justifyContent:"center",padding:"24px" }}>
          <div style={{ textAlign:"center",maxWidth:360 }}>
            <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1.3rem",color:C.bark,marginBottom:16 }}>{error}</div>
            <button className="btn-p" onClick={() => navigate("/")}>Back to dashboard</button>
          </div>
        </div>
      </>
    );
  }

  const sc = STATUS_COLORS[application.status] || STATUS_COLORS.pending;
  const sm = STATUS_MESSAGES[application.status] || STATUS_MESSAGES.pending;
  const opp = opportunity;
  const type = (opp?.type && TYPE_STYLE[opp.type]) || null;

  let artworks = [];
  let note = "";
  if (application.image_captions) {
    try {
      const parsed = JSON.parse(application.image_captions);
      artworks = parsed.artworks || [];
      note = parsed.note || "";
    } catch {}
  }

  const inp = { background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6,padding:"9px 11px",fontSize:".83rem",color:C.bark,width:"100%",boxSizing:"border-box",outline:"none",fontFamily:"inherit" };
  const lbl = { fontSize:".62rem",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,display:"block",marginBottom:4 };

  const agrAlreadyAccepted = agreement?.artist_confirmed === true;

  return (
    <>
      <style>{css}</style>

      <nav className="topnav">
        <button
          onClick={() => navigate("/")}
          style={{ background:"none",border:"none",padding:"6px 0",cursor:"pointer",display:"flex",alignItems:"center",gap:7,color:C.mutedL,fontFamily:"'DM Mono',monospace",fontSize:".72rem",letterSpacing:"1px" }}
        >
          ← Back
        </button>
      </nav>

      <div style={{ maxWidth:720,margin:"0 auto",padding:"28px 24px 80px" }}>

        {/* Opportunity header */}
        <div style={{ marginBottom:24 }}>
          {type && (
            <div style={{ marginBottom:8 }}>
              <span style={{ background:type.bg,color:type.color,fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600 }}>{type.label}</span>
            </div>
          )}
          <h1 style={{ fontFamily:"'Fraunces',serif",fontSize:"1.7rem",fontWeight:400,color:C.bark,marginBottom:6 }}>{opp?.title || "Open Call"}</h1>
          <div style={{ fontSize:".78rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>
            {[opp?.org_name, opp?.location, opp?.deadline ? `Deadline ${formatDate(opp.deadline)}` : null].filter(Boolean).join(" · ")}
          </div>
        </div>

        {/* Status message banner */}
        <div style={{ background:sm.bg,border:`1px solid ${sm.border}`,borderRadius:8,padding:"18px 20px",marginBottom:24 }}>
          <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:8 }}>
            <span style={{ fontSize:"1.1rem" }}>{sm.icon}</span>
            <span style={{ background:sc.bg,color:sc.c,fontFamily:"'DM Mono',monospace",fontSize:".56rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600 }}>{application.status}</span>
          </div>
          <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1rem",color:C.bark,marginBottom:5 }}>{sm.heading}</div>
          <div style={{ fontSize:".84rem",color:C.mutedD,lineHeight:1.65 }}>{sm.body}</div>
        </div>

        {/* Consignment agreement section — shown whenever opportunity offers consignment */}
        {opp?.consignment_offered && (
          <div style={{ border:`1.5px solid ${C.sage}`,borderRadius:10,overflow:"hidden",marginBottom:28 }}>

            {/* Agreement header */}
            <div style={{ background:`linear-gradient(120deg,${C.sageD},${C.sage})`,padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center" }}>
              <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,.85)" }}>Consignment Agreement</div>
              <span style={{
                background: !agreement ? C.parchD : agrAlreadyAccepted ? "rgba(255,255,255,.25)" : C.amberL,
                color:       !agreement ? C.mutedD : agrAlreadyAccepted ? "#fff"                 : C.amber,
                fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",padding:"3px 9px",borderRadius:"2px",fontWeight:600
              }}>
                {!agreement ? "Pending gallery" : agrAlreadyAccepted ? "Terms accepted" : "Awaiting your review"}
              </span>
            </div>

            <div style={{ background:C.cream,padding:"18px" }}>

              {!agreement ? (
                /* No agreement created by gallery yet */
                <div style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:C.parch,border:`1px solid ${C.sand}`,borderRadius:6 }}>
                  <span style={{ fontSize:"1rem",opacity:.5 }}>⏳</span>
                  <div>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",letterSpacing:"1px",textTransform:"uppercase",color:C.muted,marginBottom:2 }}>Awaiting confirmation from gallery</div>
                    <div style={{ fontSize:".8rem",color:C.muted,lineHeight:1.55 }}>The gallery will send consignment terms once they've reviewed your application.</div>
                  </div>
                </div>
              ) : agrAlreadyAccepted ? (
                /* Confirmed state */
                <>
                  {/* Terms grid — read-only summary */}
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:10 }}>Agreed terms</div>
                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                      {[
                        ["Commission", agreement.commission ? `${agreement.commission}%` : "—"],
                        ["Duration", agreement.consignment_duration ? `${agreement.consignment_duration} weeks` : "—"],
                        ["Damage policy", agreement.damage_policy || "—"],
                        ["Return shipping", agreement.return_shipping || "—"],
                      ].map(([k, v]) => (
                        <div key={k} style={{ background:C.parch,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"9px 11px" }}>
                          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>{k}</div>
                          <div style={{ fontSize:".82rem",color:C.bark,fontWeight:500,textTransform:"capitalize" }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {agreement.payment_terms && (
                      <div style={{ background:C.parch,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"9px 11px",marginTop:8 }}>
                        <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>Payment terms</div>
                        <div style={{ fontSize:".82rem",color:C.bark }}>{agreement.payment_terms}</div>
                      </div>
                    )}
                  </div>
                  <div style={{ background:C.sageL,border:`1px solid ${C.sage}`,borderRadius:6,padding:"12px 14px",display:"flex",alignItems:"center",gap:10 }}>
                    <span style={{ fontSize:"1rem" }}>✓</span>
                    <div>
                      <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",letterSpacing:"1px",textTransform:"uppercase",color:C.sageD,marginBottom:2 }}>
                        Terms acknowledged{agreement.artist_confirmed_at ? ` · ${formatDate(agreement.artist_confirmed_at.slice(0,10))}` : ""}
                      </div>
                      {(agreement.delivery_method || agreement.preferred_timing) && (
                        <div style={{ fontSize:".78rem",color:C.sageD }}>
                          {DELIVERY_OPTIONS.find(o => o.value === agreement.delivery_method)?.label}
                          {agreement.delivery_method && agreement.preferred_timing ? " · " : ""}
                          {agreement.preferred_timing}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                /* Agreement exists — needs artist acknowledgement */
                <>
                  {/* Terms grid */}
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".58rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:10 }}>Terms</div>
                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                      {[
                        ["Commission", agreement.commission ? `${agreement.commission}%` : "—"],
                        ["Duration", agreement.consignment_duration ? `${agreement.consignment_duration} weeks` : "—"],
                        ["Damage policy", agreement.damage_policy || "—"],
                        ["Return shipping", agreement.return_shipping || "—"],
                      ].map(([k, v]) => (
                        <div key={k} style={{ background:C.parch,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"9px 11px" }}>
                          <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>{k}</div>
                          <div style={{ fontSize:".82rem",color:C.bark,fontWeight:500,textTransform:"capitalize" }}>{v}</div>
                        </div>
                      ))}
                    </div>
                    {agreement.payment_terms && (
                      <div style={{ background:C.parch,border:`1px solid ${C.parchDD}`,borderRadius:5,padding:"9px 11px",marginTop:8 }}>
                        <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".54rem",letterSpacing:"1.5px",textTransform:"uppercase",color:C.muted,marginBottom:3 }}>Payment terms</div>
                        <div style={{ fontSize:".82rem",color:C.bark }}>{agreement.payment_terms}</div>
                      </div>
                    )}
                  </div>

                  <div style={{ borderTop:`1px solid ${C.sand}`,marginBottom:16 }}/>

                  <div style={{ marginBottom:14 }}>
                    <label style={lbl}>Delivery method</label>
                    <select
                      value={deliveryMethod}
                      onChange={e => setDeliveryMethod(e.target.value)}
                      style={{ ...inp,cursor:"pointer" }}
                    >
                      <option value="">Select…</option>
                      {DELIVERY_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom:18 }}>
                    <label style={lbl}>Preferred timing</label>
                    <input
                      style={inp}
                      value={preferredTiming}
                      onChange={e => setPreferredTiming(e.target.value)}
                      placeholder="e.g. Week of 12 May, or flexible"
                    />
                  </div>

                  {agrError && (
                    <div style={{ background:C.roseL,border:`1px solid ${C.rose}`,borderRadius:5,padding:"10px 14px",marginBottom:14,fontSize:".8rem",color:C.rose }}>⚠ {agrError}</div>
                  )}

                  <button
                    onClick={handleAcknowledge}
                    disabled={agrSaving}
                    style={{ width:"100%",padding:"13px",fontSize:".72rem",borderRadius:6,border:"none",background:C.sageL,color:C.sageD,cursor:agrSaving?"wait":"pointer",fontFamily:"'DM Mono',monospace",letterSpacing:"1px",textTransform:"uppercase",fontWeight:600,opacity:agrSaving?0.7:1 }}
                  >
                    {agrSaving ? "Saving…" : "Acknowledge & accept terms →"}
                  </button>
                </>
              )}

            </div>
          </div>
        )}

        {/* Submitted date */}
        <div style={{ fontFamily:"'DM Mono',monospace",fontSize:".62rem",color:C.muted,letterSpacing:"1px",textTransform:"uppercase",marginBottom:28 }}>
          Submitted {formatDate(application.submitted_at?.slice(0, 10))}
        </div>

        {/* Artworks */}
        {artworks.filter(a => a.title).length > 0 && (
          <div style={{ marginBottom:28 }}>
            <span className="slbl">Submitted works</span>
            <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
              {artworks.filter(a => a.title).map((aw, i) => (
                <div key={i} style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,overflow:"hidden" }}>
                  {aw.imageUrl && (
                    <img src={aw.imageUrl} alt={aw.title} style={{ width:"100%",maxHeight:320,objectFit:"cover",display:"block" }}/>
                  )}
                  <div style={{ padding:"14px 16px" }}>
                    <div style={{ fontFamily:"'Fraunces',serif",fontSize:"1rem",color:C.bark,marginBottom:6 }}>{aw.title}</div>
                    <div style={{ display:"flex",gap:14,flexWrap:"wrap",marginBottom:aw.description?8:0 }}>
                      {aw.medium && <span style={{ fontSize:".72rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>{aw.medium}</span>}
                      {aw.year && <span style={{ fontSize:".72rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>{aw.year}</span>}
                      {aw.dimensions && <span style={{ fontSize:".72rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>{aw.dimensions}</span>}
                      {aw.price && <span style={{ fontSize:".72rem",color:C.muted,fontFamily:"'DM Mono',monospace" }}>AUD {aw.price}</span>}
                    </div>
                    {aw.description && <p style={{ fontSize:".84rem",color:C.mutedD,lineHeight:1.65,margin:0 }}>{aw.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Artist statement */}
        {application.artist_statement && (
          <div style={{ marginBottom:24 }}>
            <span className="slbl">Artist statement</span>
            <div style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px 18px" }}>
              <p style={{ fontSize:".88rem",color:C.bark,lineHeight:1.75,margin:0 }}>{application.artist_statement}</p>
            </div>
          </div>
        )}

        {/* Theme response */}
        {application.theme_response && (
          <div style={{ marginBottom:24 }}>
            <span className="slbl">Response to theme</span>
            {opp?.theme && (
              <div style={{ fontSize:".75rem",color:C.muted,fontStyle:"italic",marginBottom:10,lineHeight:1.6,paddingLeft:12,borderLeft:`2px solid ${C.sand}` }}>{opp.theme}</div>
            )}
            <div style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px 18px" }}>
              <p style={{ fontSize:".88rem",color:C.bark,lineHeight:1.75,margin:0 }}>{application.theme_response}</p>
            </div>
          </div>
        )}

        {/* Note to gallery */}
        {note && (
          <div style={{ marginBottom:24 }}>
            <span className="slbl">Note to gallery</span>
            <div style={{ background:C.cream,border:`1px solid ${C.sand}`,borderRadius:8,padding:"16px 18px" }}>
              <p style={{ fontSize:".88rem",color:C.bark,lineHeight:1.75,margin:0 }}>{note}</p>
            </div>
          </div>
        )}

        {/* Mediums */}
        {application.selected_mediums?.length > 0 && (
          <div style={{ marginBottom:24 }}>
            <span className="slbl">Mediums</span>
            <div style={{ display:"flex",gap:7,flexWrap:"wrap" }}>
              {application.selected_mediums.map(m => (
                <span key={m} style={{ background:C.parch,border:`1px solid ${C.sand}`,borderRadius:4,padding:"5px 10px",fontSize:".72rem",color:C.mutedD,fontFamily:"'DM Mono',monospace" }}>{m}</span>
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
